import Cookies from 'js-cookie';
import { SignInData } from 'sigma-mappings';
import Http from '@/services/http';
import store from '@/store';
import router, { LOGIN_ROUTE, getReturnToQueryString } from '@/router';
import ApplicationDataService from '@/services/applicationDataService';
import { accessPending } from '@/domain/enums/login';
import UserService from './user';

const urls = {
  login: '/autenticacao',
  refresh: '/autenticacao/refresh-token',
  delete: '/autenticacao/refresh-token/revogar',
  newToken: '/autenticacao/novoToken',
  externalLoginUrl: '/autenticacao/externa/url',
  externalLogin: '/autenticacao/externa',
};

/**
 * Verifica as pendencias de acesso do usuário, como aceite de licença ou acesso temporário.
 * @param {*} signInData Dados de acesso
 */
function verifyAccessPending(signInData) {
  if (signInData.needsRedefinePassword) {
    store.dispatch('setAccessPending', accessPending.TEMP_ACCESS);
  }
  if (!signInData.licenseAccepted) {
    store.dispatch('setAccessPending', accessPending.LICENSE);
  }
}

/**
 * Define a sessão de acesso.
 * @param {String} sessionToken Token da sessão.
 * @param {Object} apiUserData Dados do usuário retornados pelo back-end.
 * @param {Object} signInData Dados do acesso.
 */
function setSession(sessionToken, apiUserData, signInData) {
  store.dispatch('setToken', sessionToken);

  // Salva a data de expiração do refreshToken se estiver presente
  if (signInData.refreshToken?.dataHoraExpiracao) {
    store.dispatch('setRefreshTokenExpiration', signInData.refreshToken.dataHoraExpiracao);
  }

  UserService.setUserData(apiUserData);
  verifyAccessPending(signInData);
}

/**
 * Limpa a sessão de acesso atual.
 */
function clearSession() {
  store.dispatch('setToken', null);
  store.dispatch('setRefreshTokenExpiration', null);
  store.dispatch('clearAccessPending');
  UserService.clearUserData();
}

/**
 * Redefine a sessão corrente, atualizando o token de acesso.
 * @param {String} newSessionToken Novo token da sessão.
 * @param {String} newExpirationDate Nova data de expiração do refresh token.
 */
function setStoreTokenData(newSessionToken, newExpirationDate) {
  store.dispatch('setToken', newSessionToken);
  store.dispatch('setRefreshTokenExpiration', newExpirationDate);
}

/*
   * Faz o redirecionamento para a tela de login na aplicação,
   * iniciando a sessão e limpando a sessão.
   */
function redirectToLogin(hasTimedOut = false) {
  // Telas de painel permanecem exibidas e mostram o próprio aviso de sessão
  // expirada quando a sessão expira sozinha (timeout). Um painel de parede que
  // se transforma em tela de login sai da vigilância silenciosamente, e não há
  // ninguém por perto para reautenticar. Isso não se aplica a um logout
  // explícito do usuário, que deve sempre redirecionar para o login.
  if (hasTimedOut && router.currentRoute.meta && router.currentRoute.meta.keepOnSessionExpired) {
    clearSession();
    return;
  }

  router.push({
    ...LOGIN_ROUTE,
    params: {
      hasTimedOut,
    },
    ...(hasTimedOut ? {
      query: getReturnToQueryString(router.currentRoute),
    } : null),
  });

  // Mesmo que o back-end não consiga encerrar corretamente a sessão, os dados serão limpos
  // no front-end, fazendo com que a sessão seja perdida localmente.
  clearSession();

  ApplicationDataService.refreshConfiguration();
}

export default {
  /**
   * Faz o login na aplicação, iniciando a sessão.
   * @param {{ username, password }} param Dados do usuário.
   * @param {String} param.username Nome do usuário.
   * @param {String} param.password Senha do usuário.
   */
  login({ username, password }) {
    store.dispatch('setRefreshTokenExpired', false);

    const http = new Http({
      errorHandling: {
        responseMessageParams: {
          $ME003: data => ({ remainingAttempts: data.parameters?.tentativasRestantes ?? 0 }),
          $ME004: () => ({ username }),
        },
      },
    });
    return http.post(urls.login, { usuario: username, chaveAcesso: password }).then(({ data }) => {
      const signInData = new SignInData(data.dadosAutenticacao);
      setSession(data.dadosAutenticacao.tokenAcesso, data.dadosUsuario, signInData);

      return signInData;
    });
  },
  redirectToLogin,
  /**
   * Faz o logout da aplicação, limpando os dados da sessão e redirecionando o usuário para a tela
   * de login.
   * @param { Boolean } hasTimedOut Logout devido a timeout.
   */
  logout(hasTimedOut = false) {
    const http = new Http({
      errorHandling: {
        ignore: true,
      },
    });
    return http.delete(urls.delete)
      // Independente do sucesso no back-end, o front-end confirmará a saída, limpando a sessão
      // localmente;
      .catch(() => null)
      .then(() => {
        // Redirecionando para o login antes de continuar, afim de fechar as telas antes de limpar
        // os dados localmente da sessão.
        redirectToLogin(hasTimedOut);
      });
  },

  /**
   * Atualiza a sessão, renovando o token de acesso junto ao back-end.
   */
  refreshSession() {
    const http = new Http({
      errorHandling: {
        ignore: true,
      },
    });
    return http.post(urls.refresh)
      .then((response) => {
        setStoreTokenData(response.data.novoToken, response.data.dataHoraExpiracaoRefreshToken);

        return response;
      });
  },

  /**
   * Indica se há sessão gravada neste navegador. O token fica num cookie
   * compartilhado entre as abas, então um login feito em outra aba aparece aqui.
   * @returns {Boolean}
   */
  hasStoredSession() {
    return !!Cookies.get('token');
  },

  /**
   * Obtem novo token de acesso para usuário logado.
   */
   newToken() {
    const http = new Http({
      errorHandling: {
        ignore: true,
      },
    });
    return http.get(urls.newToken)
      .then((response) => {
        setStoreTokenData(response.data.novoToken, response.data.dataHoraExpiracaoRefreshToken);

        return response;
      });
  },

  /**
   * Obtém a URL de redirecionamento para interface de login da Microsoft.
   * @param {String} organizationId Nome do provedor.
   */
  getExternalLoginUrl(organizationId) {
    const http = new Http({
      errorHandling: {
        ignore: true,
      },
    });

    return http
      .get(urls.externalLoginUrl, { params: { organizacaoId: organizationId } })
      .then((response) => response);
  },

  /**
   * Faz login por meio de provider externo.
   * @param {String} providerName Nome do provedor.
   * @param {String} organizationId Identificador do cliente/organização.
   * @param {String} pkceCode Código PKCE.
   */
  externalLogin(providerName, organizationId, pkceCode) {
    const http = new Http();
    store.dispatch('setRefreshTokenExpired', false);

    return http
      .post(urls.externalLogin,
        { nomeProvedor: providerName, organizacaoId: organizationId, codigoPkce: pkceCode })
      .then(({ data }) => {
        const signInData = new SignInData(data.dadosAutenticacao);
        setSession(data.dadosAutenticacao.tokenAcesso, data.dadosUsuario, signInData);
        return signInData;
      });
  },
};
